import { Button } from '@src/design-system/button';
import {
  DialogBody,
  DialogClose,
  DialogFooter,
  DialogHeader,
} from '@src/design-system/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@src/design-system/tooltip';
import type { FC } from 'react';
import Crown from '../../../assets/icons/shared/crown.gif';
import { useNavigate } from 'react-router-dom';
import { createRoute, PATHS } from '@src/core/router/paths';
import { useGetMe } from '@shared/services/get-me';
import useCheckSubscription from '@shared/hooks/use-check-subscription';
import { mockedPlansAndPricing } from '@features/settings/pages/billing/pricing/mock-data';

const PlanDialog: FC<{ freeTrialEnded?: boolean }> = ({ freeTrialEnded }) => {
  const navigate = useNavigate();
  const { data: member } = useGetMe();
  const { subscription } = useCheckSubscription();

  const selectedPlan = mockedPlansAndPricing.find(
    (plan) => plan.title === subscription?.plan?.name
  );

  return (
    <>
      <DialogHeader className='bg-primary-50' />
      <DialogBody className='flex items-center flex-col bg-primary-50 gap-5'>
        <div className='bg-white rounded-3xl p-4 px-5 text-4xl'>
          {freeTrialEnded ? (
            <img
              src={Crown}
              alt='crown'
              style={{ width: '56px', height: '56px' }}
            />
          ) : (
            '👋'
          )}
        </div>
        <div>
          <p className='text-center text-base text-gray-700'>
            {freeTrialEnded
              ? 'Your free trial has ended'
              : 'Welcome to ticlick'}
            ,{' '}
            <span className='font-semibold'>
              {member?.first_name} {member?.last_name}
            </span>{' '}
            <br />
          </p>
          <p className='text-center text-sm font-normal text-gray-700'>
            {freeTrialEnded
              ? 'Your data is safely stored. To continue using Ticlick and regain full access, please upgrade your plan.'
              : 'Your 14-days free trial has started'}
          </p>
        </div>
        <div className='text-center py-8 px-4 rounded-xl bg-white border border-primary-200 w-full flex flex-col items-center gap-5'>
          <h3 className='uppercase text-primary-1000 text-2xl font-bold'>
            {subscription?.plan?.name}
          </h3>
          <p className='text-gray-800 font-normal text-base'>
            {subscription?.plan?.description}
          </p>
          <div>
            <p className='text-gray-600 font-normal font-base mb-0 line-through'>
              $ {selectedPlan?.strikePrice}
            </p>
            <p className='text-gray-800 font-light'>
              ${' '}
              <span className='font-semibold text-5xl'>
                {selectedPlan?.yearlyPrice || 0}
              </span>
            </p>
          </div>
          <p className='text-gray-600 font-normal text-sm flex justify-center gap-2'>
            <span>Price/Org/Month Billed Annually</span>
            <Tooltip>
              <TooltipTrigger>
                <span className='rounded-full border-gray-600 border w-5 h-5 block cursor-pointer'>
                  ?
                </span>
              </TooltipTrigger>
              <TooltipContent>Pay Upfront - Commit Annually</TooltipContent>
            </Tooltip>
          </p>
        </div>
        <p className='font-normal text-sm text-gray-700'>
          You can change your plan anytime.
        </p>
      </DialogBody>
      <DialogFooter className='bg-primary-50 rounded-b-md'>
        {freeTrialEnded ? (
          <Button
            variant='secondary'
            className='w-full'
            onClick={() =>
              navigate(
                `${createRoute(PATHS.SETTINGS_BILLING_A_SUBSCRIPTION)}?tab=upgrade`
              )
            }
          >
            Upgrade Now
          </Button>
        ) : (
          <div className='flex flex-col gap-3 w-full'>
            <Button
              className='w-full'
              onClick={() => navigate(createRoute(PATHS.DASHBOARDS))}
            >
              Go to your Dashboard
            </Button>
            <DialogClose>
              <Button
                className='w-full border-primary-200 text-gray-800'
                variant='primaryOutline'
              >
                Upgrade Now
              </Button>
            </DialogClose>
          </div>
        )}
      </DialogFooter>
    </>
  );
};

export default PlanDialog;
